import { ReactNode, useState } from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Tag, Typography } from "antd";

import PortfolioDeltaArrowIcon from "@/assets/icons/portfolio_delta_arrow.svg?react";
import PageHeader from "@/components/UI/PageHeader";

import { formatNumberWithGrouping } from "@/utils/numberFormatting";

export type PortfolioMetricDelta = {
  value: number;
  invert?: boolean;
};

export type PortfolioStatusColor = "green" | "blue" | "gray" | "default";

export type PortfolioTrialHeaderInfo = {
  trial_id: string;
  name: string;
  phase: string;
  asset: string;
  studyType: string;
  status: string;
  statusColor: PortfolioStatusColor;
};

export type PortfolioAdditionalStat = {
  label: string;
  value: string;
  range: string;
} | null;

// Local to this file. Exporting it made shared.tsx a mixed module, which trips
// react-refresh/only-export-components and (with --max-warnings=0) blocked any
// commit touching the file. Portfolio/components.tsx keeps its own copy.
const PORTFOLIO_PROGRESS_BAR_COLORS = {
  blue: "#64BDE1",
  gold: "#BCA048",
  magenta: "#C275A1",
} as const;

type PortfolioProgressBarTone = keyof typeof PORTFOLIO_PROGRESS_BAR_COLORS;

export function PortfolioPageShell({
  title,
  tooltipText,
  children,
  headerExtra,
}: {
  title: string;
  tooltipText: string;
  children: ReactNode;
  headerExtra?: ReactNode;
}) {
  return (
    <div className="w-full p-4">
      <div className="relative z-20 mb-4 flex items-center justify-between">
        <div className="flex gap-2">
          <PageHeader title={title} tooltipText={tooltipText} />
        </div>
        {headerExtra}
      </div>

      {children}
    </div>
  );
}

export function PortfolioCardsList({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>;
}

export function PortfolioCardShell({
  header,
  metricColumns,
  additionalColumns,
  initiallyExpanded = true,
  children,
  additionalContent,
}: {
  header: ReactNode;
  metricColumns: number;
  additionalColumns?: number;
  initiallyExpanded?: boolean;
  children: ReactNode;
  additionalContent?: ReactNode;
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);

  return (
    <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      {header}

      <div
        className="grid divide-x divide-[#F0F2F4] border-b border-[#F0F2F4]"
        style={{
          gridTemplateColumns: `repeat(${metricColumns}, minmax(0, 1fr))`,
        }}
      >
        {children}
      </div>

      {expanded && additionalContent ? (
        <div
          className="grid items-stretch divide-x divide-[#F0F2F4] border-b border-[#F0F2F4]"
          style={{
            gridTemplateColumns: `repeat(${additionalColumns ?? metricColumns}, minmax(0, 1fr))`,
          }}
        >
          {additionalContent}
        </div>
      ) : null}

      <PortfolioExpandCollapseFooter
        expanded={expanded}
        onToggle={() => setExpanded((current) => !current)}
      />
    </div>
  );
}

export function PortfolioTrialHeader({
  trial,
  titleClassName = "!font-semibold",
  statusNode,
}: {
  trial: PortfolioTrialHeaderInfo;
  titleClassName?: string;
  statusNode?: ReactNode;
}) {
  return (
    <div className="flex h-[45px] items-center justify-between gap-4 border-b border-[#F0F2F4] px-[18px]">
      <div className="flex min-w-0 items-center gap-2.5">
        <Typography.Title
          level={5}
          className={`!mb-0 !text-[15px] !leading-5 !text-[#1F2933] ${titleClassName}`}
        >
          {trial.name}
        </Typography.Title>
        <Tag className="m-0 h-5 rounded-[4px] border-0 bg-[#E8EEF2] px-1.5 py-0 text-[13px] leading-5 text-[#48636F]">
          {trial.phase}
        </Tag>
        <Typography.Text className="text-[13px] leading-4 text-[#6B7280]">
          {trial.asset}
        </Typography.Text>
        <span className="h-1 w-1 rounded-full bg-[#BDBDBD]" />
        <Typography.Text className="truncate text-[13px] leading-4 text-[#8C8C8C]">
          {trial.studyType}
        </Typography.Text>
      </div>
      {statusNode ?? (
        <PortfolioStatusTag status={trial.status} color={trial.statusColor} />
      )}
    </div>
  );
}

export function PortfolioStatusTag({
  status,
  color,
}: {
  status: string;
  color: PortfolioStatusColor;
}) {
  // No status, no pill. Sites and trials routinely carry a blank status, and a
  // coloured pill with nothing in it reads as a rendering fault.
  if (!status?.trim()) return null;

  const toneClass =
    color === "green"
      ? "bg-[#CAE6D6] text-[#0D433C]"
      : color === "blue"
        ? "bg-[#D2EFF9] text-[#064C68]"
        : "bg-[#D2EFF9] text-[#064C68]";

  return (
    <span
      className={`inline-flex h-5 items-center rounded-full px-2 text-[12px] font-semibold leading-5 whitespace-nowrap ${toneClass}`}
    >
      {status}
    </span>
  );
}

export function PortfolioDeltaLine({
  delta,
  note,
  neutralClassName = "text-[#556C75]",
  positiveClassName = "text-[#4BA06E]",
  negativeClassName = "text-[#C2410C]",
}: {
  delta?: PortfolioMetricDelta;
  note?: string;
  neutralClassName?: string;
  positiveClassName?: string;
  negativeClassName?: string;
}) {
  if (note) {
    return <span className={`italic ${neutralClassName}`}>{note}</span>;
  }

  if (!delta) return null;

  const isNoChange = delta.value === 0;
  const isUp = delta.value > 0;
  const isDown = delta.value < 0;
  const isSemanticallyPositive = delta.invert ? isDown : isUp;
  const isSemanticallyNegative = delta.invert ? isUp : isDown;
  const deltaClass = isNoChange
    ? `flex items-center gap-1 ${neutralClassName}`
    : isSemanticallyPositive
      ? `flex items-center gap-1 ${positiveClassName}`
      : isSemanticallyNegative
        ? `flex items-center gap-1 ${negativeClassName}`
        : `flex items-center gap-1 ${neutralClassName}`;
  const deltaLabel = isNoChange
    ? "no change"
    : `${delta.value > 0 ? "+" : ""}${formatNumberWithGrouping(delta.value)}`;

  return (
    <div className={deltaClass}>
      {isNoChange ? (
        <span className="text-[14px] leading-none">—</span>
      ) : (
        <PortfolioDeltaArrowIcon
          className="h-[9px] w-[13px] shrink-0"
          style={isDown ? { transform: "scaleY(-1)" } : undefined}
          aria-hidden="true"
        />
      )}
      <span>{deltaLabel}</span>
    </div>
  );
}

export function PortfolioEmptyAdditionalStatTile() {
  return (
    <div className="relative h-full min-h-[76px] bg-[#fbfbfc]">
      <div className="absolute inset-0 flex items-center justify-center">
        <Typography.Text className="text-[10px] leading-3 text-[rgba(0,0,0,0.45)]">
          No additional stats
        </Typography.Text>
      </div>
    </div>
  );
}

export function PortfolioProgressBar({
  value,
  tone = "blue",
}: {
  value: number;
  tone?: PortfolioProgressBarTone;
}) {
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-[rgba(0,0,0,0.06)]">
      <div
        className="h-1 rounded-full"
        style={{
          width: `${Math.max(0, Math.min(100, value))}%`,
          backgroundColor: PORTFOLIO_PROGRESS_BAR_COLORS[tone],
        }}
      />
    </div>
  );
}

export function PortfolioMetricTile({
  label,
  value,
  suffix,
  suffixClassName = "text-[14px] text-[#2B4752]",
  delta,
  note,
  progress,
  progressTone,
  showComparisonPeriod = true,
  neutralDeltaClassName,
  positiveDeltaClassName,
  negativeDeltaClassName,
}: {
  label: string;
  value: ReactNode;
  suffix?: string;
  suffixClassName?: string;
  delta?: PortfolioMetricDelta;
  note?: string;
  progress?: number;
  progressTone?: PortfolioProgressBarTone;
  showComparisonPeriod?: boolean;
  neutralDeltaClassName?: string;
  positiveDeltaClassName?: string;
  negativeDeltaClassName?: string;
}) {
  const shouldShowComparisonPeriod = Boolean(
    delta && !note && showComparisonPeriod,
  );

  return (
    <div className="flex h-[96px] flex-col gap-[6px] px-[17px] py-3">
      <span className="block text-[12px] font-medium uppercase leading-none tracking-[-0.12px] text-[#A5A5A5]">
        {label}
      </span>
      <div className="flex min-h-0 flex-1 flex-col justify-between">
        <div className="flex items-end gap-1 whitespace-nowrap leading-none">
          <span className="text-[20px] font-semibold text-[#0F1923]">
            {value}
          </span>
          {suffix ? (
            <span className={`font-normal leading-none ${suffixClassName}`}>
              {suffix}
            </span>
          ) : null}
        </div>
        {progress !== undefined ? (
          <PortfolioProgressBar value={progress} tone={progressTone} />
        ) : null}
      </div>
      <div className="flex min-h-[14px] items-center justify-between text-[12px] font-medium leading-none tracking-[-0.12px]">
        <PortfolioDeltaLine
          delta={delta}
          note={note}
          neutralClassName={neutralDeltaClassName}
          positiveClassName={positiveDeltaClassName}
          negativeClassName={negativeDeltaClassName}
        />
        <span
          className={`font-normal text-[rgba(0,0,0,0.25)] ${shouldShowComparisonPeriod ? "opacity-100" : "opacity-0"}`}
        >
          30d
        </span>
      </div>
    </div>
  );
}

export function PortfolioAdditionalStatTile({
  stat,
}: {
  stat: PortfolioAdditionalStat;
}) {
  if (!stat) {
    return <PortfolioEmptyAdditionalStatTile />;
  }

  const [average, deviation] = stat.value.split(" ± ");

  return (
    <div className="flex h-full min-h-[76px] flex-col justify-center bg-[#fbfbfc] px-5 py-3">
      <Typography.Text className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8EA0BF]">
        {stat.label}
      </Typography.Text>
      <div className="mt-1 text-[14px] font-semibold leading-[18px] text-[#102033]">
        {average}
        {deviation ? (
          <span className="text-[11px] font-normal text-[#6B7280]">
            {" "}
            ± {deviation}
          </span>
        ) : null}
      </div>
      <Typography.Text className="text-[10px] leading-3 text-[#8EA0BF]">
        Range {stat.range}
      </Typography.Text>
    </div>
  );
}

function PortfolioExpandCollapseFooter({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex h-6 items-center justify-center">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1 text-[12px] font-medium leading-5 text-[#8EA0BF] hover:text-[#5F789C]"
      >
        {expanded ? (
          <UpOutlined className="text-[10px]" />
        ) : (
          <DownOutlined className="text-[10px]" />
        )}
        {expanded ? "Hide statistics" : "Show statistics"}
      </button>
    </div>
  );
}
